import React, { useState } from 'react';
import { AppTheme } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import './CashierWorkplace.css';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  total: number;
}

interface CashierWorkplaceProps {
  theme: AppTheme;
  products: any[];
}

export const CashierWorkplace: React.FC<CashierWorkplaceProps> = ({ theme, products }) => {
  const { t } = useLanguage();
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftNumber, setShiftNumber] = useState<string>('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'qr' | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Фильтрация товаров по поисковому запросу
  const filteredProducts = products.filter(product =>
    !product.is_deleted &&
    (product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
     product.code?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Добавление товара в корзину
  const addToCart = (product: any) => {
    const existingItem = cart.find(item => item.id === product.id);

    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      ));
    } else {
      setCart([...cart, {
        id: product.id,
        name: product.name,
        price: 100, // Здесь должна быть реальная цена из товара
        quantity: 1,
        total: 100
      }]);
    }
    setSearchQuery('');
    setSelectedProductIndex(0);
  };

  // Обработчик клавиш для поиска товаров
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredProducts.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedProductIndex(prev =>
          prev < filteredProducts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedProductIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredProducts[selectedProductIndex]) {
          addToCart(filteredProducts[selectedProductIndex]);
        }
        break;
    }
  };

  // Изменение количества товара
  const updateQuantity = (id: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(id);
      return;
    }

    setCart(cart.map(item =>
      item.id === id
        ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
        : item
    ));
  };

  // Удаление товара из корзины
  const removeFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };

  // Очистка корзины
  const clearCart = () => {
    setCart([]);
    setPaymentMethod(null);
    setCashAmount('');
  };

  // Подсчет итоговой суммы
  const totalAmount = cart.reduce((sum, item) => sum + item.total, 0);

  // Открытие смены
  const openShift = () => {
    const newShiftNumber = `${Date.now().toString().slice(-6)}`;
    setShiftNumber(newShiftNumber);
    setShiftOpen(true);
  };

  // Закрытие смены
  const closeShift = () => {
    if (cart.length > 0) {
      alert('Завершите текущую продажу перед закрытием смены');
      return;
    }
    setShiftOpen(false);
    setShiftNumber('');
  };

  // Обработка оплаты
  const handlePayment = () => {
    if (!paymentMethod || cart.length === 0) return;

    if (paymentMethod === 'cash' && !cashAmount) {
      return;
    }

    setShowPaymentModal(true);
    setIsProcessingPayment(true);

    // Симуляция обработки платежа
    setTimeout(() => {
      setIsProcessingPayment(false);
      setTimeout(() => {
        setShowPaymentModal(false);
        alert(`Оплата успешно завершена! Чек на сумму ${totalAmount} сом`);
        clearCart();
      }, 1000);
    }, 5000);
  };

  // Подсчет сдачи
  const getChange = () => {
    const cash = parseFloat(cashAmount) || 0;
    return cash - totalAmount;
  };

  // Если смена не открыта - показываем форму открытия смены
  if (!shiftOpen) {
    return (
      <div className={`cashier-workplace ${theme.isDark ? 'dark' : 'light'}`}>
        <div className="cashier-shift-closed">
          <div className={`cashier-shift-card ${theme.isDark ? 'dark' : 'light'}`}>
            <div className="cashier-shift-icon">🏪</div>
            <h2>{t('cashier.shift.closed')}</h2>
            <p>{t('cashier.shift.open')}</p>
            <button className="cashier-open-shift-btn" onClick={openShift}>
              {t('cashier.shift.open.button')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`cashier-workplace ${theme.isDark ? 'dark' : 'light'}`}>
      <div className="cashier-header">
        <div>
          <h2 className={`cashier-title ${theme.isDark ? 'dark' : 'light'}`}>
            {t('cashier.title')}
          </h2>
          <div className="cashier-shift-info">
            {t('cashier.shift.number')} #{shiftNumber}
          </div>
        </div>
        <button className="cashier-close-shift-btn" onClick={closeShift}>
          {t('cashier.shift.close.button')}
        </button>
      </div>

      <div className="cashier-content">
        {/* Левая панель - Поиск и товары */}
        <div className="cashier-left">
          <div className={`cashier-search-panel ${theme.isDark ? 'dark' : 'light'}`}>
            <input
              type="text"
              className={`cashier-search-input ${theme.isDark ? 'dark' : 'light'}`}
              placeholder={t('cashier.search.placeholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedProductIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
          </div>

          {searchQuery && (
            <div className={`cashier-products-list ${theme.isDark ? 'dark' : 'light'}`}>
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className={`cashier-product-item ${theme.isDark ? 'dark' : 'light'} ${index === selectedProductIndex ? 'selected' : ''}`}
                    onClick={() => addToCart(product)}
                  >
                    <div className="cashier-product-info">
                      <div className="cashier-product-name">{product.name}</div>
                      <div className="cashier-product-code">{product.code}</div>
                    </div>
                    <div className="cashier-product-price">100 {t('cashier.currency')}</div>
                  </div>
                ))
              ) : (
                <div className="cashier-no-results">{t('cashier.no.results')}</div>
              )}
            </div>
          )}
        </div>

        {/* Правая панель - Корзина и оплата */}
        <div className="cashier-right">
          <div className={`cashier-cart ${theme.isDark ? 'dark' : 'light'}`}>
            <div className="cashier-cart-header">
              <h3>{t('cashier.cart.title')}</h3>
              {cart.length > 0 && (
                <button
                  className="cashier-clear-btn"
                  onClick={clearCart}
                >
                  {t('cashier.cart.clear')}
                </button>
              )}
            </div>

            <div className="cashier-cart-items">
              {cart.length === 0 ? (
                <div className="cashier-cart-empty">
                  {t('cashier.cart.empty')}
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} className={`cashier-cart-item ${theme.isDark ? 'dark' : 'light'}`}>
                    <div className="cashier-cart-item-info">
                      <div className="cashier-cart-item-name">{item.name}</div>
                      <div className="cashier-cart-item-price">{item.price} {t('cashier.currency')}</div>
                    </div>
                    <div className="cashier-cart-item-controls">
                      <button
                        className="cashier-qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        −
                      </button>
                      <span className="cashier-qty">{item.quantity}</span>
                      <button
                        className="cashier-qty-btn"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <div className="cashier-cart-item-total">
                      {item.total} {t('cashier.currency')}
                    </div>
                    <button
                      className="cashier-remove-btn"
                      onClick={() => removeFromCart(item.id)}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <>
                <div className={`cashier-total ${theme.isDark ? 'dark' : 'light'}`}>
                  <span className="cashier-total-label">{t('cashier.total')}</span>
                  <span className="cashier-total-amount">{totalAmount} {t('cashier.currency')}</span>
                </div>

                <div className="cashier-payment-methods">
                  <button
                    className={`cashier-payment-btn ${paymentMethod === 'cash' ? 'active' : ''} ${theme.isDark ? 'dark' : 'light'}`}
                    onClick={() => { setPaymentMethod('cash'); setCashAmount(''); }}
                  >
                    💵 {t('cashier.payment.cash')}
                  </button>
                  <button
                    className={`cashier-payment-btn ${paymentMethod === 'card' ? 'active' : ''} ${theme.isDark ? 'dark' : 'light'}`}
                    onClick={() => setPaymentMethod('card')}
                  >
                    💳 {t('cashier.payment.card')}
                  </button>
                  <button
                    className={`cashier-payment-btn ${paymentMethod === 'qr' ? 'active' : ''} ${theme.isDark ? 'dark' : 'light'}`}
                    onClick={() => setPaymentMethod('qr')}
                  >
                    📱 {t('cashier.payment.qr')}
                  </button>
                </div>

                {paymentMethod === 'cash' && (
                  <div className={`cashier-cash-input-group ${theme.isDark ? 'dark' : 'light'}`}>
                    <label>{t('cashier.payment.cash.received')}</label>
                    <input
                      type="number"
                      className={`cashier-cash-input ${theme.isDark ? 'dark' : 'light'}`}
                      placeholder={t('cashier.payment.cash.amount.placeholder')}
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                      autoFocus
                    />
                    {cashAmount && parseFloat(cashAmount) >= totalAmount && (
                      <div className="cashier-change">
                        <span>{t('cashier.payment.cash.change')}</span>
                        <strong>{getChange().toFixed(2)} {t('cashier.currency')}</strong>
                      </div>
                    )}
                  </div>
                )}

                <button
                  className={`cashier-complete-btn ${(!paymentMethod || (paymentMethod === 'cash' && (!cashAmount || parseFloat(cashAmount) < totalAmount))) ? 'disabled' : ''}`}
                  onClick={handlePayment}
                  disabled={!paymentMethod || (paymentMethod === 'cash' && (!cashAmount || parseFloat(cashAmount) < totalAmount))}
                >
                  {t('cashier.payment.button')} {totalAmount} {t('cashier.currency')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно оплаты */}
      {showPaymentModal && (
        <>
          <div className="cashier-payment-overlay" />
          <div className={`cashier-payment-modal ${theme.isDark ? 'dark' : 'light'}`}>
            <div className="cashier-payment-modal-content">
              {paymentMethod === 'card' && (
                <>
                  <div className="cashier-payment-icon">💳</div>
                  <h3>{t('cashier.payment.card.title')}</h3>
                  {isProcessingPayment ? (
                    <>
                      <p>{t('cashier.payment.card.processing')}</p>
                      <div className="cashier-payment-loader">
                        <div className="cashier-payment-spinner"></div>
                      </div>
                      <p className="cashier-payment-amount">{totalAmount} {t('cashier.currency')}</p>
                    </>
                  ) : (
                    <>
                      <div className="cashier-payment-success">✅</div>
                      <p>{t('cashier.payment.success')}</p>
                    </>
                  )}
                </>
              )}

              {paymentMethod === 'qr' && (
                <>
                  <div className="cashier-payment-icon">📱</div>
                  <h3>{t('cashier.payment.qr.title')}</h3>
                  {isProcessingPayment ? (
                    <>
                      <p>{t('cashier.payment.qr.scan')}</p>
                      <div className="cashier-qr-code">
                        <div className="cashier-qr-placeholder">
                          <svg width="200" height="200" viewBox="0 0 200 200">
                            <rect width="200" height="200" fill="white"/>
                            <rect x="20" y="20" width="60" height="60" fill="black"/>
                            <rect x="120" y="20" width="60" height="60" fill="black"/>
                            <rect x="20" y="120" width="60" height="60" fill="black"/>
                            <rect x="35" y="35" width="30" height="30" fill="white"/>
                            <rect x="135" y="35" width="30" height="30" fill="white"/>
                            <rect x="35" y="135" width="30" height="30" fill="white"/>
                            <rect x="100" y="100" width="20" height="20" fill="black"/>
                            <rect x="140" y="100" width="20" height="20" fill="black"/>
                            <rect x="100" y="140" width="20" height="20" fill="black"/>
                          </svg>
                        </div>
                      </div>
                      <p className="cashier-payment-amount">{totalAmount} {t('cashier.currency')}</p>
                    </>
                  ) : (
                    <>
                      <div className="cashier-payment-success">✅</div>
                      <p>{t('cashier.payment.received')}</p>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
